import { baseApi as api } from './baseApi';

/** Una pregunta de MercadoLibre sobre una publicación nuestra. */
export interface MeliPregunta {
  preguntaId:   string;
  itemId:       string;
  propertyId:   string | null;
  texto:        string;
  estado:       'pendiente' | 'respondida' | 'cerrada';
  /** `lead` = el contacto automático de ML; `pregunta` = alguien la escribió. */
  tipo:         'lead' | 'pregunta';
  preguntadaEl: string;
  respuesta:    { texto: string; enviadaEl: string } | null;
  /** A la publicación, para verla como la ve el cliente. */
  link:         string;
}

/**
 * Una pregunta en la bandeja de Integraciones: la misma, con quién preguntó (nombre y
 * teléfono, de ML), el agente a cargo y la respuesta precargada. (04/11/2026)
 */
export interface MeliPreguntaBandeja extends Omit<MeliPregunta, 'respuesta'> {
  titulo:    string | null;
  /** Foto de portada y precio de la propiedad del aviso. */
  foto:      string | null;
  precio:    string | null;
  cliente:   { nombre: string | null; telefono: string | null } | null;
  agente:    { id: string; nombre: string; telefono: string | null } | null;
  respuesta: { texto: string; enviadaEl: string; por?: string | null } | null;
  /** Con el agente y su teléfono. Se precarga en el campo; el agente la puede cambiar. */
  sugerida:  string;
}

/** Alguien que tocó "WhatsApp" en un aviso de ML. `pendiente` = no escribió. (04/11/2026) */
export type EstadoProspecto = 'pendiente' | 'escribio' | 'contactado' | 'descartado';
export interface MeliProspecto {
  id: string; itemId: string; propertyId: string | null; titulo: string | null;
  /** Foto de portada y precio de la propiedad del aviso. */
  foto: string | null; precio: string | null;
  nombre: string | null; telefono: string | null; email: string | null;
  primerClicEl: string; ultimoClicEl: string; clics: number;
  estado: EstadoProspecto; escribioEl: string | null;
  gestion: { por: string | null; el: string; nota?: string } | null;
  agente: { id: string; nombre: string } | null;
  /** Para empezar la charla por WhatsApp desde el celular del agente. */
  mensaje: string;
  link: string;
  /** Si su teléfono ya es cliente del CRM (de quien sea): no se carga de nuevo. */
  cliente: { id: string; nombre: string; agente: string | null } | null;
}

export const meliPreguntasApi = api.injectEndpoints({
  endpoints: (build) => ({
    getMeliPreguntas: build.query<MeliPregunta[], { propertyId?: string; soloPendientes?: boolean }>({
      query: ({ propertyId, soloPendientes }) => ({
        url: '/integrations/meli/preguntas',
        params: { ...(propertyId ? { propertyId } : {}), ...(soloPendientes === false ? { soloPendientes: 'false' } : {}) },
      }),
      transformResponse: (res: { data: MeliPregunta[] }) => res.data,
      providesTags: ['MeliPreguntas'],
    }),
    /** Las que le tocan a quien mira: las suyas, las de su oficina o las del tenant. */
    getMeliBandeja: build.query<MeliPreguntaBandeja[], { todas?: boolean } | void>({
      // `todas`: también las respondidas y las que se cerraron sin respuesta (el admin las
      // mira para controlar). Sin eso, solo las pendientes: el menú y la home cuentan esas.
      query: (arg) => ({ url: '/integrations/meli/preguntas', params: arg && arg.todas ? { soloPendientes: 'false' } : {} }),
      transformResponse: (res: { data: MeliPreguntaBandeja[] }) => res.data,
      providesTags: ['MeliPreguntas'],
    }),
    getMeliProspectos: build.query<MeliProspecto[], { estados?: EstadoProspecto[] } | void>({
      query: (arg) => ({ url: '/integrations/meli/prospectos', params: arg && arg.estados?.length ? { estado: arg.estados.join(',') } : {} }),
      transformResponse: (res: { data: MeliProspecto[] }) => res.data,
      providesTags: ['MeliProspectos'],
    }),
    marcarMeliProspecto: build.mutation<{ ok: boolean }, { id: string; estado: EstadoProspecto; nota?: string }>({
      query: ({ id, estado, nota }) => ({ url: `/integrations/meli/prospectos/${id}`, method: 'PATCH', body: { estado, nota } }),
      invalidatesTags: ['MeliProspectos'],
    }),
    /** "Agregar como cliente": el backend valida antes que el teléfono no sea ya cliente (409 + `existe`). */
    agregarProspectoComoCliente: build.mutation<{ id: string; nombre: string }, { id: string }>({
      query: ({ id }) => ({ url: `/integrations/meli/prospectos/${id}/cliente`, method: 'POST' }),
      transformResponse: (res: { data: { id: string; nombre: string } }) => res.data,
      invalidatesTags: ['MeliProspectos', 'Customer'],
    }),
    responderMeliPregunta: build.mutation<{ ok: boolean }, { preguntaId: string; texto: string }>({
      query: ({ preguntaId, texto }) => ({
        url: `/integrations/meli/preguntas/${preguntaId}/responder`,
        method: 'POST',
        body: { texto },
      }),
      // La respuesta cambia el estado de la pregunta: se recarga la lista de la ficha.
      invalidatesTags: ['MeliPreguntas'],
    }),
  }),
});

export const {
  useGetMeliPreguntasQuery, useGetMeliBandejaQuery, useResponderMeliPreguntaMutation,
  useGetMeliProspectosQuery, useMarcarMeliProspectoMutation, useAgregarProspectoComoClienteMutation,
} = meliPreguntasApi;
